/* Persistent, variant-aware wishlist shared by product, header and wishlist page. */
(() => {
  const STORAGE_KEY = 'smuly.wishlist.v2';
  const EVENT = 'wishlist:change';

  class Wishlist {
    constructor() {
      this.items = this.read();
      document.addEventListener('click', this.onClick.bind(this));
      // Preserve normal link behavior while protecting the header link from theme click handlers.
      document.addEventListener('click', (event) => {
        const link = event.target.closest?.('[data-wishlist-link]');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        window.location.assign(link.href);
      }, true);
      document.addEventListener('change', (event) => {
        if (event.target.matches('form [name="id"]')) this.syncButtons();
      });
      document.addEventListener('variant:update', () => window.setTimeout(() => this.syncButtons()));
      window.addEventListener('storage', (event) => {
        if (event.key === STORAGE_KEY) this.setItems(this.read(), false);
      });
      document.addEventListener(EVENT, () => this.render());
      this.render();
    }

    read() {
      try {
        const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        return Array.isArray(data) ? data.filter((item) => item && item.variantId && item.handle) : [];
      } catch (_) { return []; }
    }

    setItems(items, persist = true) {
      this.items = items;
      if (persist) {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (_) { return; }
      }
      document.dispatchEvent(new CustomEvent(EVENT, { detail: { items: this.items } }));
    }

    has(variantId) { return this.items.some((item) => String(item.variantId) === String(variantId)); }
    toggle(item) {
      const existing = this.items.find((saved) => String(saved.productId) === String(item.productId));
      if (!existing) {
        this.setItems([...this.items, item]);
      } else if (String(existing.variantId) === String(item.variantId)) {
        this.setItems(this.items.filter((saved) => String(saved.productId) !== String(item.productId)));
      } else {
        // Keep one entry per product while preserving the currently selected variant.
        this.setItems(this.items.map((saved) => String(saved.productId) === String(item.productId) ? item : saved));
      }
    }
    remove(variantId) { this.setItems(this.items.filter((item) => String(item.variantId) !== String(variantId))); }

    getSelectedVariantId(button, product) {
      const details = button.closest('.product-details');
      const productId = button.dataset.productId;
      const formVariantId = productId
        ? document.querySelector(`product-form-component[data-product-id="${productId}"] input[name="id"]`)?.value
        : null;
      return formVariantId || details?.querySelector('form [name="id"]')?.value || button.dataset.wishlistVariantId || product.selected_or_first_available_variant?.id;
    }

    buildProduct(button, product) {
      try {
        const selectedId = this.getSelectedVariantId(button, product);
        const variant = product.variants.find((candidate) => String(candidate.id) === String(selectedId)) || product.variants[0];
        if (!variant) return null;
        return {
          productId: String(product.id), variantId: String(variant.id), handle: product.handle, title: product.title,
          variantTitle: variant.title === 'Default Title' ? '' : variant.title,
          url: `${product.url}?variant=${variant.id}`,
          image: variant.featured_image?.src || product.featured_image?.src || product.featured_image || product.images?.[0]?.src || product.images?.[0] || '',
          price: variant.price, compareAtPrice: variant.compare_at_price, available: variant.available !== false
        };
      } catch (_) { return null; }
    }

    getProduct(button) {
      const productJson = button.closest('.product-details')?.querySelector('[data-wishlist-product]')?.textContent;
      if (!productJson) return null;
      try { return this.buildProduct(button, JSON.parse(productJson)); } catch (_) { return null; }
    }

    async resolveProduct(button) {
      const embeddedProduct = this.getProduct(button);
      if (embeddedProduct) return embeddedProduct;

      // Fallback for section-rendered/custom product layouts where JSON markup is replaced.
      const handle = button.dataset.productHandle;
      if (!handle) return null;
      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return null;
        return this.buildProduct(button, await response.json());
      } catch (_) { return null; }
    }

    onClick(event) {
      const toggle = event.target.closest('[data-wishlist-toggle]');
      if (toggle) {
        event.preventDefault();
        this.resolveProduct(toggle).then((product) => {
          if (product) this.toggle(product);
        });
        return;
      }
      const remove = event.target.closest('[data-wishlist-remove]');
      if (remove) { event.preventDefault(); this.remove(remove.dataset.wishlistRemove); return; }
      const add = event.target.closest('[data-wishlist-add-to-cart]');
      if (add) { event.preventDefault(); this.addToCart(add); }
    }

    render() { this.renderCount(); this.syncButtons(); document.querySelector('wishlist-dashboard')?.render(); }
    renderCount() {
      document.querySelectorAll('[data-wishlist-count]').forEach((node) => {
        node.textContent = this.items.length;
        node.classList.toggle('wishlist-bubble--visible', this.items.length > 0);
      });
      const label = `Wishlist, ${this.items.length} ${this.items.length === 1 ? 'item' : 'items'}`;
      document.querySelectorAll('[data-wishlist-count-label]').forEach((node) => { node.textContent = label; });
      document.querySelectorAll('[data-wishlist-link]').forEach((node) => node.setAttribute('aria-label', label));
    }
    syncButtons() {
      document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
        const item = this.getProduct(button); const active = Boolean(item && this.has(item.variantId));
        button.dataset.active = String(active); button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-label', `${active ? 'Remove' : 'Add'} ${item?.title || 'product'} ${active ? 'from' : 'to'} wishlist`);
        const text = button.querySelector('[data-wishlist-button-text]');
        if (text) text.textContent = active ? 'Remove from wishlist' : 'Add to wishlist';
      });
    }

    async addToCart(button) {
      const variantId = button.dataset.wishlistAddToCart;
      if (!variantId || button.disabled) return;
      button.disabled = true; button.setAttribute('aria-busy', 'true');
      const originalText = button.textContent; button.textContent = 'Adding…';
      try {
        const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((node) => node.dataset.sectionId))];
        const response = await fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds, sections_url: window.location.pathname })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || 'Unable to add this item to cart.');
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((result) => result.json());
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: data, data: { itemCount: cart.item_count, source: 'wishlist', variantId, sections: data.sections || {} } } }));
        document.querySelector('cart-drawer-component')?.open?.();
        button.textContent = 'Added to cart';
      } catch (error) { button.textContent = error.message || 'Could not add to cart'; }
      finally {
        window.setTimeout(() => { button.disabled = false; button.removeAttribute('aria-busy'); button.textContent = originalText; }, 1600);
      }
    }
  }

  class WishlistDashboard extends HTMLElement {
    connectedCallback() { this.render(); }
    render() {
      const wishlist = window.ShopifyWishlistEngine;
      if (!wishlist) return;
      this.setAttribute('aria-busy', 'false');
      this.innerHTML = wishlist.items.length ? `<div class="wishlist-grid-container">${wishlist.items.map((item) => this.card(item)).join('')}</div>` : this.empty();
    }
    money(cents) { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.Shopify?.currency?.active || 'INR' }).format((Number(cents) || 0) / 100); }
    card(item) {
      const image = item.image ? `<img src="${this.escape(item.image)}" alt="${this.escape(item.title)}" loading="lazy" width="600" height="600">` : '';
      const compare = Number(item.compareAtPrice) > Number(item.price) ? `<s class="wishlist-price-compare">${this.money(item.compareAtPrice)}</s>` : '';
      const unavailable = item.available === false;
      return `<article class="wishlist-product-card"><a class="wishlist-image-wrap" href="${this.escape(item.url)}">${image}</a><div class="wishlist-card-details"><h2 class="wishlist-card-title"><a href="${this.escape(item.url)}">${this.escape(item.title)}</a></h2>${item.variantTitle ? `<p class="wishlist-card-variant">${this.escape(item.variantTitle)}</p>` : ''}<div class="wishlist-card-price-wrapper"><span class="wishlist-price-regular">${this.money(item.price)}</span>${compare}</div><div class="wishlist-action-btns"><a class="wishlist-btn-view-product" href="${this.escape(item.url)}">View product</a><button class="wishlist-btn-add-cart" type="button" data-wishlist-add-to-cart="${this.escape(item.variantId)}" ${unavailable ? 'disabled aria-disabled="true"' : ''}>${unavailable ? 'Sold out' : 'Add to cart'}</button><button class="wishlist-btn-remove" type="button" data-wishlist-remove="${this.escape(item.variantId)}">Remove</button></div></div></article>`;
    }
    escape(value) { const element = document.createElement('div'); element.textContent = value || ''; return element.innerHTML; }
    empty() { return '<div class="wishlist-empty-state"><h2>Your Wishlist is Empty</h2><p>Save products you love and find them here anytime.</p><a href="/collections/all" class="wishlist-btn-continue">Continue Shopping</a></div>'; }
  }

  if (!customElements.get('wishlist-dashboard')) customElements.define('wishlist-dashboard', WishlistDashboard);

  // Some stores have an existing Wishlist page assigned to the default page template.
  // Render the dashboard there too, so the saved products are always visible.
  const ensureWishlistPage = () => {
    const path = window.location.pathname.replace(/\/$/, '').toLowerCase();
    const isWishlistPage = path.endsWith('/pages/wishlist') || path.endsWith('/pages/wishlist-page');
    if (!isWishlistPage || document.querySelector('wishlist-dashboard')) return;

    const main = document.querySelector('#MainContent');
    if (!main) return;
    const wrapper = document.createElement('section');
    wrapper.className = 'section page-width-content wishlist-section wishlist-section--fallback';
    wrapper.setAttribute('data-wishlist-page', '');
    wrapper.innerHTML = '<div class="wishlist-page__inner"><wishlist-dashboard aria-live="polite" aria-busy="true"></wishlist-dashboard></div>';
    main.append(wrapper);
  };

  ensureWishlistPage();
  window.ShopifyWishlistEngine = new Wishlist();
})();
